import React, { useMemo } from "react";

import { usePlaceData } from "../hooks/usePlaceData";
import { useProductFilters } from "../hooks/useProductFilters";
import type { ReadyConfig, ViewState } from "../shared/types";
import type { Place } from "../types/warehouse.types";
import { BenchmarkButton } from "./BenchmarkButton";
import { CustomerPalletsFilter } from "./CustomerPalletsFilter";
import { CustomerProductFilter } from "./CustomerProductFilter";
import { FPSToggleButton } from "./FPSToggleButton";
import { LabelControls } from "./LabelControls";
import { LevelFilter } from "./LevelFilter";
import { OtherControls } from "./OtherControls";
import { PlaceInfo } from "./PlaceInfo";
import { PlaceSearchPanel } from "./PlaceSearchPanel";
import { PortInfo } from "./PortInfo";
import { RowFilter } from "./RowFilter";
import { T1Filter } from "./T1Filter";
import { TerminalOverview } from "./TerminalOverview";
import { ViewBase } from "./ViewBase";

interface ViewProps {
  readyConfig: ReadyConfig;
  terminalId: number;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  urlSelectedPlaceIds: string[];
  hasPersistedView?: boolean;
}

export const View: React.FC<ViewProps> = (props) => {
  const { places: placesRecord } = props.readyConfig;

  const places = useMemo(() => {
    const entries = Object.entries(placesRecord).map(([placeId, place]) => [
      placeId,
      { ...place, place_id: placeId }
    ]);
    return new Map(entries as [string, Place][]);
  }, [placesRecord]);

  const { allPlaces, allCustomers, allPalletCustomers } = usePlaceData(places);

  // Use product filters hook to manage all filter state and logic
  const {
    selectedCustomer,
    selectedProduct,
    handleCustomerChange,
    handleProductChange,
    availableProducts,
    selectedPalletCustomer,
    selectedPalletProduct,
    handlePalletCustomerChange,
    handlePalletProductChange,
    availablePalletProducts,
    selectedT1,
    handleT1Change,

    filterPlace: extraFilter,
  } = useProductFilters({ allPlaces });

  return (
    <ViewBase
      {...props}
      extraFilter={extraFilter}
      renderSidePanel={(sp) => (
        <>
          <PlaceSearchPanel terminalId={sp.terminalId} onSearch={sp.handleSearch} />

          <TerminalOverview
            groups={sp.terminalOverviewStats.groups}
          />

          <PlaceInfo 
            selectedPlaces={sp.selectedPlaces} 
            terminalId={sp.terminalId} 
            storageEntities={sp.storageEntities} 
          />

          <T1Filter
            value={selectedT1}
            onChange={handleT1Change}
          />

          <CustomerPalletsFilter
            allPalletCustomers={allPalletCustomers}
            selectedPalletCustomer={selectedPalletCustomer}
            handlePalletCustomerChange={handlePalletCustomerChange}
            availablePalletProducts={availablePalletProducts}
            selectedPalletProduct={selectedPalletProduct}
            handlePalletProductChange={handlePalletProductChange}
          />


          <LevelFilter
            allLevels={sp.allLevels}
            activeLevels={sp.activeLevels}
            onToggleLevel={sp.toggleLevel}
            onSelectAll={sp.selectAllLevels}
            onClearAll={sp.clearAllLevels}
          />

          <RowFilter
            rows={sp.rowOptions}
            activeRows={sp.activeRows}
            onToggleRow={sp.toggleRow}
            onSelectAll={sp.selectAllRows}
            onClearAll={sp.clearAllRows}
          />

          <LabelControls
            showAreaLabels={sp.showAreaLabels} 
            showRowLabels={sp.showRowLabels} 
            showPortLabels={sp.showPortLabels}
            onToggleAreaLabels={() => sp.setShowAreaLabels(!sp.showAreaLabels)}
            onToggleRowLabels={() => sp.setShowRowLabels(!sp.showRowLabels)}
            onTogglePortLabels={() => sp.setShowPortLabels(!sp.showPortLabels)}
            onToggleAllLabels={() => { sp.setShowAreaLabels(true); sp.setShowRowLabels(true); sp.setShowPortLabels(true); }}
            onToggleNoneLabels={() => { sp.setShowAreaLabels(false); sp.setShowRowLabels(false); sp.setShowPortLabels(false); }}
          />

          <PortInfo selectedPorts={sp.allSelectedPorts} terminalId={sp.terminalId} />

          <CustomerProductFilter
            allCustomers={allCustomers}
            selectedCustomer={selectedCustomer}
            handleCustomerChange={handleCustomerChange}
            availableProducts={availableProducts}
            selectedProduct={selectedProduct}
            handleProductChange={handleProductChange}
          />

          <OtherControls terminalId={sp.terminalId} />

          <BenchmarkButton onStartBenchmark={sp.startBenchmark} isRunning={sp.benchmarkState.status === 'running'} />
          <FPSToggleButton showFPS={sp.showFPS} onToggle={sp.toggleFPS} />
        </>
      )}
    />
  );
};
